import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import "./DeleteModal.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  right: "50%",
  maxHeight: "80vh",
  overflowY: "auto",
  transform: "translate(-50%, -50%)",
  width: 480,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: "30px",
};

const DEFAULT_NOTE_MAX_LENGTH = 500;

export const DeleteModal = ({
  setDeleteOpen,
  deleteOpen,
  label,
  deleteHandler,
  deleteType = "time trial",
  /** When set, replaces the default “Do you want to delete…” / label block (e.g. level delete warning). */
  warningMessage,
  permanentWarning,
  cascadeWarning,
  noteEnabled = false,
  noteMaxLength = DEFAULT_NOTE_MAX_LENGTH,
  noteLabel = "Deletion reason (optional)",
  notePlaceholder = "Add a note about why this is being deleted",
  confirmLabel = "Yes",
  cancelLabel = "No",
  isDeleting = false,
}) => {
  const [note, setNote] = React.useState("");

  const handleClose = () => {
    if (isDeleting) return;
    setNote("");
    setDeleteOpen(false);
  };

  React.useEffect(() => {
    if (!deleteOpen) {
      setNote("");
    }
  }, [deleteOpen]);

  const confirmHandler = async () => {
    if (isDeleting) return;
    await deleteHandler(noteEnabled ? note.trim() : undefined);
    setNote("");
    setDeleteOpen(false);
  };

  return (
    <Modal
      open={deleteOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="deletemodal">
          <div className="deletemodal__header">
            <IconButton
              onClick={handleClose}
              disabled={isDeleting}
              sx={{
                position: "absolute",
                right: -24,
                top: -24,
                color: "#444444",
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="deletemodal__content">
            {warningMessage ? (
              <h3 className="deletemodal__question">{warningMessage}</h3>
            ) : (
              <>
                <h3 className="deletemodal__question">
                  Do you want to delete {deleteType}
                </h3>
                {label ? (
                  <div className="deletemodal__item">
                    <span className="deletemodal__item-text">{label}</span>
                  </div>
                ) : null}
              </>
            )}
            {cascadeWarning ? (
              <p className="deletemodal__cascadeWarning">{cascadeWarning}</p>
            ) : null}
            {permanentWarning ? (
              <p className="deletemodal__permanentWarning">{permanentWarning}</p>
            ) : null}
            {noteEnabled ? (
              <TextField
                label={noteLabel}
                placeholder={notePlaceholder}
                multiline
                minRows={3}
                fullWidth
                value={note}
                onChange={(e) =>
                  setNote(e.target.value.slice(0, noteMaxLength))
                }
                helperText={`${note.length}/${noteMaxLength}`}
                inputProps={{ maxLength: noteMaxLength }}
                sx={{ mt: 1, textAlign: "left" }}
              />
            ) : null}
          </div>

          <div className="deletemodal__action">
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isDeleting}
              sx={{
                width: "100px",
                color: "grey.600",
                borderColor: "grey.300",
                "&:hover": {
                  borderColor: "grey.400",
                  backgroundColor: "grey.50",
                },
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              onClick={confirmHandler}
              variant="contained"
              disabled={isDeleting}
              color={noteEnabled ? "error" : "primary"}
              sx={
                noteEnabled
                  ? undefined
                  : {
                      width: "100px",
                      backgroundColor: "#3361cf",
                      "&:hover": {
                        backgroundColor: "#2a4f9e",
                      },
                    }
              }
            >
              {isDeleting ? "Deleting..." : confirmLabel}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
